/**
 * Admin Priority Management
 *
 * Org admins (and ALL-org super admins, with an org picker) view and reorder
 * the ferris-wheel priority list 1..N. Reordering is done as a *local draft*
 * — drag-and-drop or by typing a new position into a member's row. The draft
 * is not persisted until Save is clicked, and Save first opens a preview
 * dialog that shows every from→to change before the admin commits.
 *
 * Concurrency: the server validates that the submitted set of user_profile_ids
 * still exactly matches the org's current priority list at apply time. If a
 * member joined or left between draft-load and apply, we surface a toast and
 * re-fetch so the admin re-creates the draft against fresh state.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowDown, ArrowUp, GripVertical, Loader2, RefreshCw, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { useAdminOrganizations, type AdminOrganization } from '@/api/extensions/useAdminOps';
import {
  usePriorityList,
  useBulkReorderPriority,
  type PriorityListItem,
  type BulkReorderDiffEntry,
} from '@/api/extensions/usePriorityOps';
import { notify } from '@/utils/notifications';

const ALL_ORG_ID = '00000000-0000-0000-0000-000000000001';
const PAGE_LIMIT = 500; // priority lists are bounded by org membership

interface DraftRow {
  user_profile_id: string;
  card_number: string;
  display_first_name: string;
  display_last_name: string;
  first_name: string;
  last_name: string;
  current_group: number | null;
  original_position: number;
}

/**
 * Resolve a member's name for display. Prefer the opt-in display name; fall
 * back to the legal name when the member hasn't set one (so the list never
 * renders "(no name)"); finally fall back to the card number.
 */
function displayName(row: {
  display_first_name?: string;
  display_last_name?: string;
  first_name?: string;
  last_name?: string;
  card_number?: string;
}) {
  const display = `${row.display_first_name ?? ''} ${row.display_last_name ?? ''}`.trim();
  if (display) return display;
  const legal = `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim();
  if (legal) return legal;
  return row.card_number ? `Card #${row.card_number}` : 'Unknown member';
}

// Color-coding for the four ferris-wheel groups (+ an "ungrouped" state for
// members who haven't been through a draw yet). Group 1 = highest priority.
const GROUP_STYLES: Record<
  number,
  { label: string; badge: string; accent: string; dot: string }
> = {
  1: {
    label: 'Group 1',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300',
    accent: 'border-l-4 border-l-emerald-500',
    dot: 'bg-emerald-500',
  },
  2: {
    label: 'Group 2',
    badge: 'bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-300',
    accent: 'border-l-4 border-l-sky-500',
    dot: 'bg-sky-500',
  },
  3: {
    label: 'Group 3',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
    accent: 'border-l-4 border-l-amber-500',
    dot: 'bg-amber-500',
  },
  4: {
    label: 'Group 4',
    badge: 'bg-rose-100 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300',
    accent: 'border-l-4 border-l-rose-500',
    dot: 'bg-rose-500',
  },
};

const UNGROUPED_STYLE = {
  label: 'No group yet',
  badge: 'bg-muted text-muted-foreground',
  accent: 'border-l-4 border-l-transparent',
  dot: 'bg-muted-foreground/40',
};

function groupStyle(group: number | null) {
  return (group != null && GROUP_STYLES[group]) || UNGROUPED_STYLE;
}

export default function PriorityManagementPage() {
  const isAllOrgAdmin = useAuthStore((s) => s.isAllOrgAdmin());
  const role = useAuthStore((s) => s.role?.role_name);

  // ─── Org selection ──────────────────────────────────────────────────────
  // Super admins pick which org's list to manage. Org-scoped admins are
  // pinned to their single org and never see the dropdown.
  const { data: orgsData, isLoading: orgsLoading } = useAdminOrganizations();
  const orgs: AdminOrganization[] = useMemo(() => {
    const list = (orgsData?.orgs ?? []) as AdminOrganization[];
    // The ALL sentinel org has no real members and would be misleading in
    // this picker. Hide it regardless of role.
    return list.filter((o) => o.id !== ALL_ORG_ID);
  }, [orgsData]);

  const [associationId, setAssociationId] = useState<string>('');

  // Auto-pick when there's only one option, or when the loaded org list
  // changes such that the current selection is no longer valid.
  useEffect(() => {
    if (orgs.length === 0) {
      if (associationId) setAssociationId('');
      return;
    }
    if (!associationId || !orgs.some((o) => o.id === associationId)) {
      setAssociationId(orgs[0].id);
    }
  }, [orgs, associationId]);

  // ─── Server list ────────────────────────────────────────────────────────
  const {
    data: listData,
    isLoading: listLoading,
    isFetching: listFetching,
    error: listError,
    refetch,
  } = usePriorityList(associationId, { limit: PAGE_LIMIT, offset: 0 });

  const serverItems: PriorityListItem[] = useMemo(
    () => listData?.items ?? [],
    [listData],
  );

  // ─── Draft state ────────────────────────────────────────────────────────
  const [draft, setDraft] = useState<DraftRow[]>([]);

  // Re-seed the draft whenever the server snapshot changes *and* the admin
  // has no in-flight edits. We don't want to clobber an in-progress draft on
  // background refetches.
  const isDirty = useMemo(() => {
    if (draft.length !== serverItems.length) return false; // can't compare yet
    return draft.some((row, idx) => row.original_position !== idx + 1);
  }, [draft, serverItems]);

  const lastServerSignatureRef = useRef<string>('');
  useEffect(() => {
    const signature = serverItems
      .map((r) => `${r.user_profile_id}:${r.priority_position}`)
      .join('|');
    if (!signature) {
      lastServerSignatureRef.current = '';
      setDraft([]);
      return;
    }
    if (signature === lastServerSignatureRef.current) return;
    // Server snapshot changed. If admin has unsaved edits, leave the draft
    // alone — Save will surface the conflict and re-fetch.
    if (isDirty) return;
    lastServerSignatureRef.current = signature;
    setDraft(
      serverItems.map((r) => ({
        user_profile_id: r.user_profile_id,
        card_number: r.card_number,
        display_first_name: r.display_first_name,
        display_last_name: r.display_last_name,
        first_name: r.first_name,
        last_name: r.last_name,
        current_group: r.current_group,
        original_position: r.priority_position,
      })),
    );
  }, [serverItems, isDirty]);

  // ─── Search filter (display only — never reorders the underlying draft) ─
  const [search, setSearch] = useState('');
  const filteredIndices = useMemo(() => {
    if (!search.trim()) return draft.map((_, i) => i);
    const q = search.trim().toLowerCase();
    return draft
      .map((row, i) => ({ i, hit: [displayName(row), row.card_number].some((s) => s.toLowerCase().includes(q)) }))
      .filter((r) => r.hit)
      .map((r) => r.i);
  }, [draft, search]);

  // ─── Drag-and-drop (native HTML5) ───────────────────────────────────────
  // No external dnd library is installed in this workspace, and the list
  // is bounded by org membership size, so a simple HTML5 implementation
  // is enough. We track the index of the dragged row and reorder on drop.
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  const moveRow = (from: number, to: number) => {
    if (from === to) return;
    setDraft((prev) => {
      if (from < 0 || from >= prev.length || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  // ─── Manual position input ──────────────────────────────────────────────
  // Local per-row input value so admins can type freely before committing
  // (so a half-typed "12" doesn't briefly try to land at row 1). Commit on
  // blur or Enter; cancel on Escape.
  const [manualPosition, setManualPosition] = useState<Record<string, string>>({});
  const commitManualPosition = (userId: string, currentIndex: number) => {
    const raw = manualPosition[userId];
    if (raw === undefined) return;
    const parsed = Number.parseInt(raw, 10);
    setManualPosition((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
    if (Number.isNaN(parsed)) return;
    const target = Math.max(1, Math.min(draft.length, parsed));
    if (target - 1 === currentIndex) return;
    moveRow(currentIndex, target - 1);
  };

  const resetDraft = () => {
    setDraft(
      serverItems.map((r) => ({
        user_profile_id: r.user_profile_id,
        card_number: r.card_number,
        display_first_name: r.display_first_name,
        display_last_name: r.display_last_name,
        first_name: r.first_name,
        last_name: r.last_name,
        current_group: r.current_group,
        original_position: r.priority_position,
      })),
    );
    setManualPosition({});
  };

  // ─── Save flow with preview ─────────────────────────────────────────────
  const bulkReorder = useBulkReorderPriority();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDiff, setPreviewDiff] = useState<BulkReorderDiffEntry[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const orderedUserProfileIds = useMemo(
    () => draft.map((r) => r.user_profile_id),
    [draft],
  );

  const changedCount = useMemo(
    () => draft.filter((r, i) => r.original_position !== i + 1).length,
    [draft],
  );

  // Per-group member counts for the analytics cards. Index 0 = ungrouped
  // (members who haven't been through a draw yet); 1..4 = the ferris-wheel
  // groups. Sourced from each member's current_group (last draw).
  const groupCounts = useMemo(() => {
    const counts = [0, 0, 0, 0, 0];
    for (const row of draft) {
      const g = row.current_group;
      counts[g != null && g >= 1 && g <= 4 ? g : 0]++;
    }
    return counts;
  }, [draft]);

  const openPreview = async () => {
    if (!associationId || changedCount === 0) return;
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await bulkReorder.mutateAsync({
        associationId,
        orderedUserProfileIds,
        dryRun: true,
      });
      // Diff comes back sorted by user; for display we'd rather see by
      // target position so the dialog reads top-to-bottom like the list.
      const sorted = [...res.diff].sort((a, b) => a.to_position - b.to_position);
      setPreviewDiff(sorted);
    } catch (err: any) {
      setPreviewOpen(false);
      const msg = err?.message || 'Failed to compute preview';
      notify.error(msg);
      // 409 means membership shifted — re-fetch so the admin can rebuild.
      if (/Membership changed|not currently on this association/i.test(msg)) {
        refetch();
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const applyChanges = async () => {
    if (!associationId) return;
    setApplying(true);
    try {
      const res = await bulkReorder.mutateAsync({
        associationId,
        orderedUserProfileIds,
        dryRun: false,
      });
      notify.success(
        res.applied
          ? `Applied — ${res.changed_count} member position${res.changed_count === 1 ? '' : 's'} updated.`
          : 'No changes to apply.',
      );
      setPreviewOpen(false);
      setPreviewDiff([]);
      // Snap each row's `original_position` to its current index so isDirty
      // flips to false immediately. Without this, the re-seed effect's
      // `if (isDirty) return` gate keeps bailing when the post-save refetch
      // lands, so the "unsaved changes" badge sticks until the user reloads.
      // Once isDirty is false, the next refetch reseeds the draft normally.
      setDraft((prev) =>
        prev.map((row, idx) => ({ ...row, original_position: idx + 1 })),
      );
      setManualPosition({});
    } catch (err: any) {
      const msg = err?.message || 'Failed to apply changes';
      notify.error(msg);
      if (/Membership changed|not currently on this association/i.test(msg)) {
        setPreviewOpen(false);
        refetch();
      }
    } finally {
      setApplying(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────
  const canEdit = role === 'admin' || role === 'delegate';
  if (!canEdit) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Admin or delegate access required to manage priorities.
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Ferris Wheel</h1>
          <p className="text-sm text-muted-foreground">
            Each member's current group and position from the last draw. Drag
            rows or type a position to reorder. Changes stay in a draft until
            you click Save changes.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {isAllOrgAdmin && orgs.length > 1 && (
            <div className="min-w-[16rem]">
              <Label className="text-xs text-muted-foreground">Organization</Label>
              <Select
                value={associationId}
                onValueChange={(value) => {
                  // Hard-switch orgs: discard the unsaved draft for the
                  // previous org. Confirming via dialog would be friendlier
                  // but in practice the picker is the primary nav for super
                  // admins and a toast keeps the flow tight.
                  if (isDirty) notify.warning('Unsaved priority draft discarded.');
                  setAssociationId(value);
                  setManualPosition({});
                }}
                disabled={orgsLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.map((o) => (
                    <SelectItem key={o.id} value={o.id}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={listFetching || !associationId}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${listFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {associationId && draft.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
          {[1, 2, 3, 4].map((g) => {
            const style = GROUP_STYLES[g];
            return (
              <Card key={g} className={style.accent}>
                <CardContent className="flex items-center justify-between p-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${style.dot}`} />
                      <span className="text-sm font-medium">{style.label}</span>
                    </div>
                    <div className="mt-1 text-2xl font-semibold tabular-nums">
                      {groupCounts[g]}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {groupCounts[0] > 0 && (
            <Card className={UNGROUPED_STYLE.accent}>
              <CardContent className="flex items-center justify-between p-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${UNGROUPED_STYLE.dot}`} />
                    <span className="text-sm font-medium">{UNGROUPED_STYLE.label}</span>
                  </div>
                  <div className="mt-1 text-2xl font-semibold tabular-nums">
                    {groupCounts[0]}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <CardTitle className="text-lg">
              Priority list
              {listData ? (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({draft.length} member{draft.length === 1 ? '' : 's'})
                </span>
              ) : null}
            </CardTitle>
            <div className="flex items-center gap-2">
              {isDirty && (
                <Badge variant="secondary">
                  {changedCount} unsaved change{changedCount === 1 ? '' : 's'}
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={resetDraft}
                disabled={!isDirty || applying}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Discard
              </Button>
              <Button
                size="sm"
                onClick={openPreview}
                disabled={!isDirty || applying || previewLoading}
              >
                Save changes
              </Button>
            </div>
          </div>
          <Input
            placeholder="Search by name or card number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
        </CardHeader>
        <CardContent>
          {!associationId && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {orgsLoading ? 'Loading organizations…' : 'No organization available to manage.'}
            </div>
          )}

          {associationId && listLoading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading priority list…
            </div>
          )}

          {/* Surface the actual error from the priority-list query. Without
              this, a failed request (e.g. a 400 from a stale query schema)
              falls through to the "No priority entries yet" empty state and
              makes the failure look like missing data instead of a bug. */}
          {associationId && !listLoading && listError && (
            <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-destructive">
              <AlertTriangle className="h-5 w-5" />
              <div>
                Couldn't load the priority list.
                <div className="text-xs text-muted-foreground">
                  {(listError as Error)?.message || 'Unknown error'}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={listFetching}
              >
                <RefreshCw className={`mr-2 h-4 w-4 ${listFetching ? 'animate-spin' : ''}`} />
                Retry
              </Button>
            </div>
          )}

          {associationId && !listLoading && !listError && draft.length === 0 && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No priority entries yet. Seed the list from the rotation tools to
              populate this org.
            </div>
          )}

          {associationId && draft.length > 0 && (
            <ul className="divide-y rounded-md border">
              {filteredIndices.map((idx) => {
                const row = draft[idx];
                const newPosition = idx + 1;
                const moved = row.original_position !== newPosition;
                const isDragSource = dragFrom === idx;
                const isDragTarget = dragOver === idx && dragFrom !== null && dragFrom !== idx;
                const manualValue =
                  manualPosition[row.user_profile_id] ?? String(newPosition);

                return (
                  <li
                    key={row.user_profile_id}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', String(idx));
                      setDragFrom(idx);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      if (dragOver !== idx) setDragOver(idx);
                    }}
                    onDragLeave={() => {
                      if (dragOver === idx) setDragOver(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = Number.parseInt(
                        e.dataTransfer.getData('text/plain'),
                        10,
                      );
                      if (!Number.isNaN(from)) moveRow(from, idx);
                      setDragFrom(null);
                      setDragOver(null);
                    }}
                    onDragEnd={() => {
                      setDragFrom(null);
                      setDragOver(null);
                    }}
                    className={[
                      'flex items-center gap-3 px-3 py-2 transition-colors',
                      groupStyle(row.current_group).accent,
                      isDragSource ? 'opacity-50' : '',
                      isDragTarget ? 'border-t-2 border-primary bg-muted/40' : '',
                      moved ? 'bg-amber-50 dark:bg-amber-950/30' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-label={`Member ${displayName(row)} at position ${newPosition}`}
                  >
                    <span
                      className="cursor-grab text-muted-foreground"
                      title="Drag to reorder"
                    >
                      <GripVertical className="h-4 w-4" />
                    </span>

                    <div className="flex w-24 items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        max={draft.length}
                        value={manualValue}
                        onChange={(e) =>
                          setManualPosition((prev) => ({
                            ...prev,
                            [row.user_profile_id]: e.target.value,
                          }))
                        }
                        onBlur={() => commitManualPosition(row.user_profile_id, idx)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.currentTarget.blur();
                          } else if (e.key === 'Escape') {
                            setManualPosition((prev) => {
                              const next = { ...prev };
                              delete next[row.user_profile_id];
                              return next;
                            });
                            e.currentTarget.blur();
                          }
                        }}
                        className="h-8 w-16 text-center"
                        aria-label={`Set position for ${displayName(row)}`}
                      />
                    </div>

                    <div className="flex flex-1 items-center gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{displayName(row)}</span>
                          <span
                            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${groupStyle(row.current_group).badge}`}
                            title={`Current ferris-wheel group from the last draw`}
                          >
                            {groupStyle(row.current_group).label}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Card #{row.card_number} · Position #{newPosition}
                        </div>
                      </div>

                      {moved ? (
                        <div className="flex items-center gap-1 text-xs">
                          <span className="text-muted-foreground">
                            was #{row.original_position}
                          </span>
                          {row.original_position > newPosition ? (
                            <ArrowUp className="h-3 w-3 text-emerald-600" />
                          ) : (
                            <ArrowDown className="h-3 w-3 text-rose-600" />
                          )}
                          <span className="font-medium">→ #{newPosition}</span>
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
              {filteredIndices.length === 0 && (
                <li className="py-6 text-center text-sm text-muted-foreground">
                  No members match the current search.
                </li>
              )}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* ─── Preview / Confirm Save dialog ──────────────────────────────── */}
      <Dialog
        open={previewOpen}
        onOpenChange={(open) => {
          if (!open && !applying) {
            setPreviewOpen(false);
            setPreviewDiff([]);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirm priority changes</DialogTitle>
            <DialogDescription>
              These positions will take effect immediately for the next draw.
              Review the from→to changes below and click Apply to commit.
            </DialogDescription>
          </DialogHeader>

          {previewLoading ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Computing preview…
            </div>
          ) : previewDiff.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No effective changes. Your draft matches the server.
            </div>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/50">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-medium">Member</th>
                    <th className="px-3 py-2 font-medium">Card</th>
                    <th className="px-3 py-2 text-right font-medium">From</th>
                    <th className="px-3 py-2 text-right font-medium">To</th>
                  </tr>
                </thead>
                <tbody>
                  {previewDiff.map((d) => (
                    <tr
                      key={d.user_profile_id}
                      className="border-t hover:bg-muted/40"
                    >
                      <td className="px-3 py-2">
                        {displayName(d)}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        #{d.card_number}
                      </td>
                      <td className="px-3 py-2 text-right text-muted-foreground">
                        #{d.from_position}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">
                        #{d.to_position}
                        {d.from_position > d.to_position ? (
                          <ArrowUp className="ml-1 inline h-3 w-3 text-emerald-600" />
                        ) : (
                          <ArrowDown className="ml-1 inline h-3 w-3 text-rose-600" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (!applying) {
                  setPreviewOpen(false);
                  setPreviewDiff([]);
                }
              }}
              disabled={applying}
            >
              Cancel
            </Button>
            <Button
              onClick={applyChanges}
              disabled={applying || previewLoading || previewDiff.length === 0}
            >
              {applying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Applying…
                </>
              ) : (
                `Apply ${previewDiff.length} change${previewDiff.length === 1 ? '' : 's'}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
